import {
  ColumnDef,
  flexRender,
  Table as TanstackTable,
} from '@tanstack/react-table';
import { clsx } from 'clsx';
import _range from 'lodash/range';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { AggregateDataCheckbox } from './aggregate-data-checkbox';
import { Skeleton } from './skeleton';
import { Typography } from './typography';

interface DataTableProps<TData, TValue> {
  table: TanstackTable<TData>;
  columns: ColumnDef<TData, TValue>[];
  isLoading?: boolean;
  emptyState?: React.ReactNode;
  showAggregateData?: boolean;
  setShowAggregateData?: (showAggregateData: boolean) => void;
}

export function DataTable<TData, TValue>({
  table,
  columns,
  isLoading = false,
  emptyState,
  showAggregateData = false,
  setShowAggregateData,
}: DataTableProps<TData, TValue>) {
  const intl = useIntl();

  const lastLeftLeafId = table.getLeftLeafColumns().at(-1)?.id;
  const hasPinnedColumns = !!lastLeftLeafId;
  const hasMultipleHeaderGroups = table.getHeaderGroups().length > 1;

  return (
    <div className="flex flex-col items-start self-stretch">
      <div className="border-fx-neutral-200 flex items-center justify-between self-stretch border-b bg-white p-4">
        <Typography variant="subtitle1">
          {`${table.getRowCount()} `}
          <FormattedMessage
            defaultMessage="Openings"
            description="Count label: number of job openings in the table"
          />
        </Typography>
        {setShowAggregateData && (
          <AggregateDataCheckbox
            checked={showAggregateData}
            onCheckedChange={setShowAggregateData}
            label={intl.formatMessage({
              defaultMessage: 'Show aggregate data',
              description:
                'Toggle: include an extra row summing the whole table',
            })}
          />
        )}
      </div>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup, groupIndex) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const isGroupRow = hasMultipleHeaderGroups && groupIndex === 0;
                // Group row: meta comes from the column itself (the group parent)
                // Leaf row: meta comes from the column's parent (the group it belongs to)
                const groupMeta = isGroupRow
                  ? header.column.columnDef.meta
                  : header.column.parent?.columnDef.meta;
                const hasGroupLabel =
                  isGroupRow && !header.isPlaceholder && groupMeta?.groupBg;

                // Narrow columns (select/menu/title/status) keep compact 8px padding.
                // All metric columns after status get 16px horizontal padding + overflow clip.
                const isNarrow = !!header.column.columnDef.meta?.narrowCell;

                const isPinned = header.column.getIsPinned();
                // Last sticky header: the header whose rightmost leaf is the last left-pinned leaf
                const headerRightmostLeafId =
                  header.column.columns.length > 0
                    ? header.column.columns.at(-1)?.id
                    : header.column.id;
                const isLastLeftPinned =
                  isPinned === 'left' &&
                  headerRightmostLeafId === lastLeftLeafId;

                // Sticky headers need an opaque background; group headers keep their
                // coloured band, everything else falls back to the thead bg.
                const headerBg =
                  isPinned === 'left'
                    ? (isGroupRow && groupMeta?.groupBg) || 'bg-fx-neutral-50'
                    : isGroupRow
                      ? groupMeta?.groupBg
                      : groupMeta?.cellBg;

                return (
                  <TableHead
                    key={header.id}
                    colSpan={header.colSpan}
                    style={
                      isPinned === 'left'
                        ? {
                            left: header.getStart(),
                            width: header.getSize(),
                            minWidth: header.getSize(),
                            maxWidth: header.getSize(),
                          }
                        : undefined
                    }
                    className={clsx(
                      headerBg,
                      isPinned === 'left' && 'sticky z-20',
                      hasPinnedColumns && !isNarrow && 'px-4',
                      hasPinnedColumns && !isNarrow && !isLastLeftPinned && 'overflow-hidden',
                      isLastLeftPinned &&
                        'border-fx-neutral-200 border-r after:pointer-events-none after:absolute after:inset-y-0 after:left-full after:w-3 after:bg-gradient-to-r after:from-black/5 after:to-transparent',
                    )}
                  >
                    {hasGroupLabel ? (
                      <div className="flex items-center justify-center">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                    ) : isGroupRow ? null : (
                      <Typography variant="subtitle1">
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </Typography>
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            _range(table.getState().pagination.pageSize).map((_, rowIndex) => {
              return (
                <TableRow
                  // eslint-disable-next-line react/no-array-index-key
                  key={`skeleton-${rowIndex}`}
                >
                  {table.getAllLeafColumns().map(col => {
                    const isPinned = col.getIsPinned();
                    const cellBg = col.parent?.columnDef.meta?.cellBg;
                    const isLastLeftPinned =
                      isPinned === 'left' && col.id === lastLeftLeafId;
                    const isNarrowCol = !!col.columnDef.meta?.narrowCell;
                    return (
                      <TableCell
                        key={col.id}
                        style={
                          isPinned === 'left'
                            ? {
                                left: col.getStart('left'),
                                width: col.getSize(),
                                minWidth: col.getSize(),
                                maxWidth: col.getSize(),
                              }
                            : undefined
                        }
                        className={clsx(
                          isPinned === 'left' ? 'sticky z-10 bg-white' : cellBg,
                          hasPinnedColumns && !isNarrowCol && 'px-4',
                          hasPinnedColumns && !isNarrowCol && !isLastLeftPinned && 'overflow-hidden',
                          isLastLeftPinned &&
                            'border-fx-neutral-200 border-r after:pointer-events-none after:absolute after:inset-y-0 after:left-full after:w-3 after:bg-gradient-to-r after:from-black/5 after:to-transparent',
                        )}
                      >
                        <Skeleton className="h-3 w-full" />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map(row => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
              >
                {row.getVisibleCells().map(cell => {
                  const isPinned = cell.column.getIsPinned();
                  const cellBg = cell.column.parent?.columnDef.meta?.cellBg;
                  const isLastLeftPinned =
                    isPinned === 'left' && cell.column.id === lastLeftLeafId;
                  const isNarrowCol = !!cell.column.columnDef.meta?.narrowCell;
                  return (
                    <TableCell
                      key={cell.id}
                      style={
                        isPinned === 'left'
                          ? {
                              left: cell.column.getStart('left'),
                              width: cell.column.getSize(),
                              minWidth: cell.column.getSize(),
                              maxWidth: cell.column.getSize(),
                            }
                          : undefined
                      }
                      className={clsx(
                        isPinned === 'left'
                          ? 'group-hover/row:bg-fx-neutral-50 group-data-[state=selected]/row:bg-fx-brand-50 sticky z-10 bg-white'
                          : cellBg,
                        hasPinnedColumns && !isNarrowCol && 'px-4',
                        hasPinnedColumns && !isNarrowCol && !isLastLeftPinned && 'overflow-hidden',
                        isLastLeftPinned &&
                          'border-fx-neutral-200 border-r after:pointer-events-none after:absolute after:inset-y-0 after:left-full after:w-3 after:bg-gradient-to-r after:from-black/5 after:to-transparent',
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {emptyState}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
